// Seeding the finished marker across a library that predates it.
//
// Only videos whose checklist is already clear are touched -- the marker is
// meant to be your judgement, so this fills in the cases where the judgement
// is not in question and leaves everything with a real gap alone.

import React, { useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  Typography,
} from '@mui/material';
import { useAdmin } from './AdminStore';
import { describeError } from './api';
import { deriveStatus } from './schema';
import { writeBool } from '../ps/format';

export function readyUnmarked(videos) {
  return Object.entries(videos || {})
    .filter(([, video]) => {
      const status = deriveStatus(video);
      return status.ready && !status.finished;
    })
    .map(([identifier, video]) => ({ identifier, title: video.title || identifier }));
}

export function BulkFinishDialog({ open, onClose }) {
  const { videos, patch, toast } = useAdmin();
  const [progress, setProgress] = useState(null);
  const [failures, setFailures] = useState([]);

  const targets = useMemo(() => readyUnmarked(videos), [videos]);

  const run = async () => {
    setFailures([]);
    setProgress({ done: 0, total: targets.length });

    const failed = [];
    // Sequential on purpose: each write re-authenticates against a separate
    // service, and 53 of those at once is a good way to get rate limited.
    for (const [index, target] of targets.entries()) {
      try {
        await patch(target.identifier, { finished: writeBool(true) });
      } catch (error) {
        failed.push({ ...target, reason: describeError(error, '') });
      }
      setProgress({ done: index + 1, total: targets.length });
    }

    setProgress(null);
    setFailures(failed);

    if (!failed.length) {
      toast(`Marked ${targets.length} finished.`, 'success');
      onClose();
    } else {
      toast(`${targets.length - failed.length} marked, ${failed.length} failed.`, 'warning');
    }
  };

  const busy = !!progress;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Mark complete videos finished</DialogTitle>
      <DialogContent dividers>
        {targets.length === 0 ? (
          <Alert severity="success">
            Nothing to seed — every video whose checklist is clear is already marked.
          </Alert>
        ) : (
          <>
            <Alert severity="info">
              <AlertTitle>{targets.length} pass every check but are unmarked</AlertTitle>
              Each gets a <code>finished</code> flag and drops out of your to-do list. Nothing
              else on the record changes, and nothing on the site reads this field. Videos with
              any gap are left alone.
            </Alert>

            <Box sx={{ maxHeight: 240, overflowY: 'auto', mt: '10px' }}>
              <List dense disablePadding>
                {targets.map((t) => (
                  <ListItem key={t.identifier} disableGutters sx={{ py: 0 }}>
                    <Typography variant="body2" sx={{ color: 'var(--ps-text-2)' }}>
                      {t.title}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Box>
          </>
        )}

        {progress && (
          <Box sx={{ mt: '18px' }}>
            <LinearProgress
              variant="determinate"
              value={(progress.done / Math.max(1, progress.total)) * 100}
            />
            <Typography className="admin-mono" sx={{ fontSize: 11, color: 'var(--ps-text-dim)', mt: '4px' }}>
              {progress.done} / {progress.total}
            </Typography>
          </Box>
        )}

        {failures.length > 0 && (
          <Alert severity="error" sx={{ mt: '10px' }}>
            <AlertTitle>{failures.length} could not be marked</AlertTitle>
            The rest went through — running this again retries only these.
            <List dense disablePadding sx={{ mt: '6px' }}>
              {failures.map((f) => (
                <ListItem key={f.identifier} disableGutters sx={{ py: 0 }}>
                  <Typography variant="body2">
                    {f.title} — {f.reason}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {failures.length ? 'Close' : 'Cancel'}
        </Button>
        {targets.length > 0 && (
          <Button variant="contained" onClick={run} disabled={busy}>
            {busy ? 'Marking…' : `Mark ${targets.length} finished`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default BulkFinishDialog;
